import { marginPlanApi } from 'app/services/api';

import { createNamedActions } from 'utils/namedActionTypesProxy';

import { extractErrorMessage } from '../marginCenter.actionUtils';

export const PLANS_ACTION_TYPE = createNamedActions('marginCenter/plans', {
  GET_MARGIN_PLANS: 'GET_MARGIN_PLANS',
  SET_PLANS_LOADING: 'SET_PLANS_LOADING',
  SET_PLANS_ERROR: 'SET_PLANS_ERROR',
  GET_MARGIN_PLAN_VERSIONS: 'GET_MARGIN_PLAN_VERSIONS',
  SET_VERSIONS_LOADING: 'SET_VERSIONS_LOADING',
  SET_VERSIONS_ERROR: 'SET_VERSIONS_ERROR',
  UPSERT_MARGIN_PLAN: 'UPSERT_MARGIN_PLAN',
  REMOVE_MARGIN_PLAN: 'REMOVE_MARGIN_PLAN',
  SET_PLAN_SAVING: 'SET_PLAN_SAVING',
  SET_PLAN_SAVE_ERROR: 'SET_PLAN_SAVE_ERROR',
  SET_PLAN_ACTIVATING: 'SET_PLAN_ACTIVATING',
  SET_PLAN_ACTIVATE_ERROR: 'SET_PLAN_ACTIVATE_ERROR',
  CLEAR_PLAN_ACTIVATE_ERROR: 'CLEAR_PLAN_ACTIVATE_ERROR',
  SET_PLAN_ACTIVATE_PENDING_CHANGE_REQUEST: 'SET_PLAN_ACTIVATE_PENDING_CHANGE_REQUEST',
  CLEAR_PLAN_ACTIVATE_PENDING_CHANGE_REQUEST: 'CLEAR_PLAN_ACTIVATE_PENDING_CHANGE_REQUEST',
});

// PPE 2.0 (DEV-4849 wave 3): the legacy /activate route now 409s with a MarginPlanActivationRequiresApprovalBody
// (message, marginChangeRequestId, thresholdTripReason) when the edit trips the guardrail threshold —
// this is a VALID outcome (a Pending change request was opened), not a hard failure, so it is reduced to
// its own state slice rather than the generic activate-error message.
const HTTP_STATUS_CONFLICT = 409;

const extractPendingChangeRequest = error => {
  const body = error?.response?.data;

  return error?.response?.status === HTTP_STATUS_CONFLICT && body?.marginChangeRequestId
    ? body
    : null;
};

export const getMarginPlansAction = () => async dispatch => {
  dispatch({ type: PLANS_ACTION_TYPE.SET_PLANS_LOADING, payload: true });

  try {
    const { data } = await marginPlanApi.getPlans();

    return dispatch({ type: PLANS_ACTION_TYPE.GET_MARGIN_PLANS, payload: data });
  } catch {
    return dispatch({ type: PLANS_ACTION_TYPE.SET_PLANS_ERROR });
  }
};

// The version chain is keyed by the plan it was fetched FOR, so a channel switch can never render the
// previous channel's chain while the new one loads (the UI drops a mismatched-planId chain).
export const getMarginPlanVersionsAction = marginPlanId => async dispatch => {
  dispatch({ type: PLANS_ACTION_TYPE.SET_VERSIONS_LOADING, payload: true });

  try {
    const { data } = await marginPlanApi.getPlanVersions(marginPlanId);

    return dispatch({
      type: PLANS_ACTION_TYPE.GET_MARGIN_PLAN_VERSIONS,
      payload: { marginPlanId, data },
    });
  } catch {
    return dispatch({ type: PLANS_ACTION_TYPE.SET_VERSIONS_ERROR });
  }
};

// Create when no marginPlanId, else update. Returns the saved plan on success, null on failure — the
// caller surfaces the reduced errorMessage inline rather than through the global toast.
export const saveMarginPlanAction =
  ({ marginPlanId, request }) =>
  async dispatch => {
    dispatch({ type: PLANS_ACTION_TYPE.SET_PLAN_SAVING, payload: true });

    try {
      const { data } = marginPlanId
        ? await marginPlanApi.updatePlan(marginPlanId, request)
        : await marginPlanApi.createPlan(request);

      dispatch({ type: PLANS_ACTION_TYPE.UPSERT_MARGIN_PLAN, payload: data });

      return data;
    } catch (error) {
      dispatch({
        type: PLANS_ACTION_TYPE.SET_PLAN_SAVE_ERROR,
        payload: extractErrorMessage(error),
      });

      return null;
    }
  };

// Returns true on success, null on failure. A failed DELETE (403/409/network) reduces the server
// message into the same inline save-error slice and the plan is retained — the caller skips its refetch.
export const deleteMarginPlanAction = marginPlanId => async dispatch => {
  try {
    await marginPlanApi.deletePlan(marginPlanId);
  } catch (error) {
    dispatch({
      type: PLANS_ACTION_TYPE.SET_PLAN_SAVE_ERROR,
      payload: extractErrorMessage(error),
    });

    return null;
  }

  dispatch({ type: PLANS_ACTION_TYPE.REMOVE_MARGIN_PLAN, payload: marginPlanId });

  return true;
};

// Returns the activated plan on a direct (in-threshold) activation, null otherwise — a threshold trip
// (409) is NOT an activation, so callers that gate on a truthy return (e.g. the Preview stepper's
// advanceOnce) correctly stay put; the pending change request is exposed separately via
// selectPlanActivatePendingChangeRequest for the UI to route to the checker queue.
export const activateMarginPlanAction = marginPlanId => async dispatch => {
  dispatch({ type: PLANS_ACTION_TYPE.SET_PLAN_ACTIVATING, payload: true });

  try {
    const { data } = await marginPlanApi.activatePlan(marginPlanId);

    dispatch({ type: PLANS_ACTION_TYPE.UPSERT_MARGIN_PLAN, payload: data });

    return data;
  } catch (error) {
    const pending = extractPendingChangeRequest(error);

    if (pending) {
      dispatch({
        type: PLANS_ACTION_TYPE.SET_PLAN_ACTIVATE_PENDING_CHANGE_REQUEST,
        payload: { marginPlanId, ...pending },
      });

      return null;
    }

    dispatch({
      type: PLANS_ACTION_TYPE.SET_PLAN_ACTIVATE_ERROR,
      payload: extractErrorMessage(error),
    });

    return null;
  }
};

export const clearPlanActivateErrorAction = () => dispatch =>
  dispatch({ type: PLANS_ACTION_TYPE.CLEAR_PLAN_ACTIVATE_ERROR });

export const clearPlanActivatePendingChangeRequestAction = () => dispatch =>
  dispatch({ type: PLANS_ACTION_TYPE.CLEAR_PLAN_ACTIVATE_PENDING_CHANGE_REQUEST });
